import {
  Box,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';

import { TextField } from '@verantos-dev/shared-components';
import { NumericOperator, type NumericRange } from '@verantos-dev/types-cohorts';

import { AVPill } from './AVPill';

// ── Operator display config ──────────────────────────────────────────────

interface OperatorOption {
  value: NumericOperator;
  symbol: string;
  label: string;
}

const SINGLE_VALUE_OPERATORS: OperatorOption[] = [
  { value: NumericOperator.EQ, symbol: '=', label: 'Equal to (=)' },
  { value: NumericOperator.NEQ, symbol: '≠', label: 'Not equal to (≠)' },
  { value: NumericOperator.LT, symbol: '<', label: 'Less than (<)' },
  { value: NumericOperator.LTE, symbol: '≤', label: 'Less than or equal (≤)' },
  { value: NumericOperator.GT, symbol: '>', label: 'Greater than (>)' },
  { value: NumericOperator.GTE, symbol: '≥', label: 'Greater than or equal (≥)' },
];

function getOperatorSymbol(operator: string): string {
  return SINGLE_VALUE_OPERATORS.find((o) => o.value === operator)?.symbol ?? operator;
}

// ── Sub-components ───────────────────────────────────────────────────────

interface OperatorPillProps {
  operator: NumericOperator;
  onChange?: (operator: NumericOperator) => void;
  readOnly?: boolean;
}

const OperatorPill = ({ operator, onChange, readOnly }: OperatorPillProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => setOpen((prev) => !prev), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleSelect = useCallback(
    (op: NumericOperator) => {
      onChange?.(op);
      setOpen(false);
    },
    [onChange]
  );

  const pill = (
    <Box ref={anchorRef} sx={{ display: 'inline-flex' }}>
      <AVPill
        data-testid="av-numeric-operator-pill"
        onClick={readOnly ? undefined : handleToggle}
        sx={{ px: 3 }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1 }}>
          {getOperatorSymbol(operator)}
        </Typography>
      </AVPill>
    </Box>
  );

  if (readOnly) return pill;

  return (
    <>
      {pill}
      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1300 }}
          >
            <Paper elevation={8} sx={{ border: 1, borderColor: 'divider', minWidth: 180 }}>
              <MenuList dense>
                {SINGLE_VALUE_OPERATORS.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    data-testid={`av-numeric-operator-option-${opt.value}`}
                    selected={opt.value === operator}
                    onClick={() => handleSelect(opt.value)}
                  >
                    <Typography variant="body2">{opt.label}</Typography>
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

interface ValuePillProps {
  value: number | null | undefined;
  onChange?: (value: number | null) => void;
  readOnly?: boolean;
}

const ValuePill = ({ value, onChange, readOnly }: ValuePillProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(String(value ?? ''));

  const handleClick = useCallback(() => {
    setInputValue(String(value ?? ''));
    setIsEditing(true);
  }, [value]);

  const handleChange = useCallback((_id: string, newValue: string) => {
    setInputValue(newValue);
  }, []);

  const handleBlur = useCallback(
    (_id: string, blurValue: string, inError: boolean) => {
      setIsEditing(false);
      if (inError || blurValue.trim() === '') {
        setInputValue(String(value ?? ''));
        return;
      }
      const parsed = Number(blurValue);
      if (Number.isFinite(parsed)) {
        onChange?.(parsed);
      } else {
        setInputValue(String(value ?? ''));
      }
    },
    [onChange, value]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        (e.target as HTMLInputElement).blur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
        setInputValue(String(value ?? ''));
      }
    },
    [value]
  );

  if (!readOnly && isEditing) {
    return (
      <Box data-testid="av-numeric-value-input" sx={{ display: 'inline-block' }}>
        <TextField
          id="av-numeric-value"
          displayLabel=""
          type="float"
          allowNegative
          width={100}
          value={inputValue}
          autoFocus
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </Box>
    );
  }

  return (
    <AVPill
      data-testid="av-numeric-value-pill"
      onClick={readOnly ? undefined : handleClick}
      sx={{ minWidth: 32, px: 2 }}
    >
      <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1 }}>
        {value ?? '?'}
      </Typography>
    </AVPill>
  );
};

// ── Main component ───────────────────────────────────────────────────────

function getLowOperator(filter: NumericRange): NumericOperator {
  if (filter.operator === NumericOperator.BETWEEN) {
    return filter.lowInclusive !== false ? NumericOperator.GTE : NumericOperator.GT;
  }
  // NOT_BETWEEN: display the exclusion operators
  return filter.lowInclusive !== false ? NumericOperator.LT : NumericOperator.LTE;
}

function getHighOperator(filter: NumericRange): NumericOperator {
  if (filter.operator === NumericOperator.BETWEEN) {
    return filter.highInclusive !== false ? NumericOperator.LTE : NumericOperator.LT;
  }
  return filter.highInclusive !== false ? NumericOperator.GT : NumericOperator.GTE;
}

interface EditableNumericFilterProps {
  filter: NumericRange;
  onChange?: (updated: NumericRange) => void;
  readOnly?: boolean;
}

export const EditableNumericFilter = ({
  filter,
  onChange,
  readOnly,
}: EditableNumericFilterProps) => {
  const isBetween =
    filter.operator === NumericOperator.BETWEEN || filter.operator === NumericOperator.NOT_BETWEEN;

  const handleOperatorChange = useCallback(
    (operator: NumericOperator) => {
      onChange?.({ ...filter, operator });
    },
    [filter, onChange]
  );

  const handleValueChange = useCallback(
    (value: number | null) => {
      onChange?.({ ...filter, value });
    },
    [filter, onChange]
  );

  const handleLowChange = useCallback(
    (low: number | null) => {
      onChange?.({ ...filter, low });
    },
    [filter, onChange]
  );

  const handleHighChange = useCallback(
    (high: number | null) => {
      onChange?.({ ...filter, high });
    },
    [filter, onChange]
  );

  if (isBetween) {
    return (
      <Box
        data-testid="av-numeric-filter-editable"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <OperatorPill
            operator={getLowOperator(filter)}
            onChange={handleOperatorChange}
            readOnly={readOnly}
          />
          <ValuePill value={filter.low} onChange={handleLowChange} readOnly={readOnly} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          and
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <OperatorPill
            operator={getHighOperator(filter)}
            onChange={handleOperatorChange}
            readOnly={readOnly}
          />
          <ValuePill value={filter.high} onChange={handleHighChange} readOnly={readOnly} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="av-numeric-filter-editable"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      <OperatorPill
        operator={filter.operator}
        onChange={handleOperatorChange}
        readOnly={readOnly}
      />
      <ValuePill value={filter.value} onChange={handleValueChange} readOnly={readOnly} />
    </Box>
  );
};

export { SINGLE_VALUE_OPERATORS, type OperatorOption };
